import * as React from 'react';
import {useEffect} from 'react';

import {CardItem, Item} from '../items/item.class';
import {ListDetailItemListItem} from '../lists/list-detail/ListDetailItemList';
import {PriceLine} from './PriceLine';
import {PriceUnit} from './price.types';
import {UsersService} from '../../client/users/users.service';
import {UserStateService} from '../../client/users/user-state.service';
import {User} from '../users/user.class';
import {useService} from '../react/ServiceContext';

interface PriceComponentProps {
    alias: string;
    boldPrice?: boolean;
    className?: string;
    doNotShowEachPrice?: boolean;
    isActionBlock?: boolean;
    isError?: boolean;
    isItemCard?: boolean;
    isItemSpinner?: boolean;
    item: CardItem | Item | ListDetailItemListItem;
    onModalDisplay?: () => void;
    returnUrl?: string;
    showCurrencyTooltip?: boolean;
    useCondensed?: boolean;
    user: User;
}

export const Price = ({
    alias,
    boldPrice = false,
    className = ``,
    doNotShowEachPrice = false,
    isActionBlock = false,
    isError = false,
    isItemCard = false,
    isItemSpinner = false,
    item,
    onModalDisplay,
    returnUrl,
    showCurrencyTooltip,
    useCondensed = false,
    user,
}: PriceComponentProps) => {
    const componentName = `price-component-${alias}-${item.item}`;
    const usersService: UsersService = useService(`usersService`);
    const userStateService: UserStateService = useService(`userStateService`);

    /**
     * Invokes logon workflow
     */
    const logon = () => {
        usersService.logon(componentName, onModalDisplay, returnUrl);
    };

    // Process queued actions
    useEffect(() => {
        if (userStateService?.hasPendingAction(componentName, `logon`)) {
            logon();
        }
        // Only run once per page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // For non view price users, do not render at all
    if (user.isLoggedIn() && !user.canViewPrice()) {
        return;
    }

    // Build priceObject
    const priceObject = {
        onSale: item.priceInfo?.specialNumber && item.priceInfo?.specialNumber !== '0',
        price: 0,
        unit: `` as PriceUnit,
    };

    /**
     * Template
     */
    if ((!user.isLoggedIn() || user.canViewPrice()) && isError) {
        return <div>There was an error loading price</div>;
    }
    if (user.isLoggedIn()) {
        if (!item.priceInfo) {
            return;
        }
        if (item.priceInfo && item.priceInfo.success === `OK`) {
            priceObject.price = item.priceInfo.priceCCalc;
            priceObject.unit = item.priceInfo.priceUnit;
        } else {
            return (
                <div
                    className={`${isItemSpinner ? 'tw-table-cell tw-leading-[14px] tw-align-bottom tw-text-sm tw-pb-2' : ''}${
                        className ? ` ${className}` : ``
                    }`}
                >
                    <PriceLine
                        boldPrice={boldPrice}
                        currency={item.priceInfo.currDesc}
                        error={item.priceInfo.error}
                        errorType={item.priceInfo.errorType}
                        isActionBlock={isActionBlock}
                        isPkgQty1={item?.pkgQty === 1}
                        isItemCard={isItemCard}
                        isItemSpinner={isItemSpinner}
                        onSale={priceObject.onSale}
                        onLogon={logon}
                        showCurrencyTooltip={showCurrencyTooltip}
                    />
                </div>
            );
        }
    } else if (item.webPrice) {
        return (
            <div
                className={`${isItemSpinner ? 'tw-table-cell tw-leading-[14px] tw-align-bottom tw-text-sm tw-pb-2' : ''}${
                    className ? ` ${className}` : ``
                }`}
            >
                <PriceLine
                    basePrice={true}
                    boldPrice={isItemSpinner ? true : boldPrice}
                    doNotShowEachPrice={doNotShowEachPrice}
                    eachPrice={item.webPrice.priceEA}
                    isActionBlock={isActionBlock}
                    isPkgQty1={item?.pkgQty === 1}
                    isItemCard={isItemCard}
                    isItemSpinner={isItemSpinner}
                    onLogon={logon}
                    onSale={false}
                    price={item.webPrice.pricePkg}
                    unit="ea"
                />
            </div>
        );
    } else {
        return (
            <PriceLine
                boldPrice={boldPrice}
                className={className}
                isActionBlock={isActionBlock}
                isItemCard={isItemCard}
                isItemSpinner={isItemSpinner}
                isPkgQty1={item?.pkgQty === 1}
                login
                onLogon={logon}
                onSale={priceObject.onSale}
            />
        );
    }
    return (
        <div
            className={`${
                isItemSpinner
                    ? `tw-table-cell tw-leading-[14px] tw-align-bottom tw-text-sm ${priceObject.onSale ? 'tw-pb-0.5' : 'tw-pb-2'}`
                    : ''
            }${className ? ` ${className}` : ``}`}
        >
            <>
                {priceObject.onSale && !useCondensed && (
                    <PriceLine
                        basePrice
                        boldPrice={false}
                        className={`tw-text-gray-400 !tw-font-normal tw-line-through${
                            isActionBlock ? ' tw-mb-2 !tw-text-xl' : ` !tw-text-sm${!isItemSpinner ? ' tw-mb-2' : ''}`
                        }`}
                        currency={item.priceInfo?.currDesc}
                        doNotShowEachPrice={doNotShowEachPrice}
                        eachPrice={item.priceInfo?.bpriceCCalc}
                        isActionBlock={isActionBlock}
                        isPkgQty1={item?.pkgQty === 1}
                        isItemCard={isItemCard}
                        isItemSpinner={isItemSpinner}
                        onLogon={logon}
                        onSale={priceObject.onSale}
                        price={item.priceInfo?.bpricePkg}
                        showCurrencyTooltip={showCurrencyTooltip}
                        unit={item.priceInfo?.bpriceUnit}
                    />
                )}
                <PriceLine
                    boldPrice={boldPrice}
                    currency={item.priceInfo?.currDesc}
                    doNotShowEachPrice={doNotShowEachPrice}
                    eachPrice={priceObject.price}
                    isActionBlock={isActionBlock}
                    isPkgQty1={item?.pkgQty === 1}
                    isItemCard={isItemCard}
                    isItemSpinner={isItemSpinner}
                    onLogon={logon}
                    onSale={priceObject.onSale}
                    price={item.priceInfo?.pricePkg}
                    showCurrencyTooltip={showCurrencyTooltip}
                    unit={priceObject.unit}
                />
            </>
        </div>
    );
};
